import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { PaperPlane } from "@/components/PaperPlane";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["All", "Meta Ads", "Brand Voice", "Local Marketing", "Video Strategy"];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = active === "All" ? posts : posts.filter((p) => p.category === active);

  return (
    <>
      <Seo
        title="Local Business Marketing Blog | Meta Ads Tips | Prolific Maven"
        description="Insights for local Florida business owners ready to own their market, Meta ads, brand voice, video strategy & local marketing tips from Prolific Maven."
        path="/blog"
      />
      <PageHero
        eyebrow="Insights"
        title="Insights for local business owners ready to own their market."
      />

      <section className="relative z-10 bg-ink-800 border-t border-white/10 py-16 sm:py-24" data-testid="blog-list">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-wrap gap-3 mb-14">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`blog-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-5 py-2 text-sm tracking-wide border transition-colors ${active === c ? "bg-gold text-ink-900 border-gold font-bold" : "border-white/15 text-cream/70 hover:border-gold/50 hover:text-white"}`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-cream/50">Loading articles…</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {filtered.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 3) * 80}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block">
                    <div className="relative overflow-hidden border border-white/10 aspect-[16/10]">
                      <img src={p.cover_image} alt={`${p.title}, Prolific Maven blog`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute top-3 left-3 bg-ink-900/90 text-gold text-xs uppercase tracking-widest px-3 py-1">{p.category}</span>
                    </div>
                    <div className="mt-5">
                      <p className="text-cream/40 text-xs uppercase tracking-widest">{p.published_at} · {p.read_time}</p>
                      <h2 className="mt-3 font-display font-bold text-xl text-white leading-snug group-hover:text-gold transition-colors">{p.title}</h2>
                      <p className="mt-3 text-cream/60 text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                      <span className="inline-flex items-center gap-2 mt-4 text-gold text-sm link-underline">Read article <PaperPlane className="w-4 h-4" /></span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection headline="Ready to amplify your voice?" subhead="Stop reading about it and start owning your market. Apply to work with Prolific Maven." />
    </>
  );
}
